import { Injectable } from '@angular/core'
import { JwtHelperService } from '@auth0/angular-jwt'
import {
    AuthenticatedResult,
    AuthOptions,
    EventTypes,
    LoginResponse,
    OidcSecurityService,
    PublicEventsService,
} from 'angular-auth-oidc-client'
import { filter, firstValueFrom, map, Observable } from 'rxjs'
import { ConfigId } from 'src/app/login/models/config-id.model'
import { UserType } from 'src/app/login/models/user-type.model'
import { SessionStorageService } from 'src/app/login/services/session-storage.service'
import { Logger } from 'src/app/utils/logger'
import { RedirectTo } from 'src/app/utils/redirect-to'
import { environment } from 'src/environments/environment'
import { Features } from '../models/features.model'

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private static readonly CONFIG_ID_STORAGE_KEY = 'configId'
    private jwtHelperService: JwtHelperService = new JwtHelperService()
    private loginResponse: LoginResponse | null = null

    constructor(
        private oidcSecurityService: OidcSecurityService,
        private logger: Logger,
        private features: Features,
        private publicEventsService: PublicEventsService,
        private sessionStorageService: SessionStorageService
    ) {
        publicEventsService
            .registerForEvents()
            .pipe(filter((notification) => notification.type === EventTypes.NewAuthenticationResult))
            .subscribe(async (n) => {
                if (n.value?.isAuthenticated === true) {
                    if (this.loginResponse != null) {
                        this.loginResponse.accessToken = await firstValueFrom(
                            oidcSecurityService.getAccessToken(this.configId)
                        )
                    }
                    this.sessionStorageService.setSessionExpired(false)
                }
            })

        publicEventsService
            .registerForEvents()
            .pipe(filter((notification) => notification.type === EventTypes.SilentRenewFailed))
            .subscribe((_) => {
                this.sessionStorageService.setSessionExpired(true)
            })
    }

    public get sessionExpired(): boolean {
        return this.sessionStorageService.sessionExpired
    }

    public get getAccessToken(): string | null {
        return this.loginResponse?.accessToken ?? null
    }

    public get isAuthenticated(): boolean {
        return this.loginResponse?.isAuthenticated ?? false
    }

    public get checkAuth(): Observable<LoginResponse> {
        return this.oidcSecurityService.checkAuth(undefined, this.configId).pipe(
            map((response) => {
                this.logger.debug('Login response:', response)
                this.loginResponse = response
                return response
            })
        )
    }

    public get isAuthenticated$(): Observable<AuthenticatedResult> {
        return this.oidcSecurityService.isAuthenticated$
    }

    public get userType(): UserType | undefined {
        if (environment.envName === 'mock') {
            return this.configId === ConfigId.business ? UserType.Business : UserType.Private
        }
        const userType = this.jwtHelperService.decodeToken(this.getAccessToken)?.UserType
        switch (userType) {
            case 'privat':
                return UserType.Private
            case 'erhverv':
                return UserType.Business
        }
        return undefined
    }

    public get configId(): ConfigId | null {
        switch (sessionStorage.getItem(AuthService.CONFIG_ID_STORAGE_KEY)) {
            case ConfigId.business.toString():
                return ConfigId.business
            case ConfigId.private.toString():
                return ConfigId.private
            default:
                return null
        }
    }

    public get isPrivateCustomer(): boolean {
        return this.userType && this.userType === UserType.Private ? true : false
    }

    public get isBusinessCustomer(): boolean {
        return this.isPrivateCustomer === false
    }

    public get isIdpUser(): boolean {
        const token = this.jwtHelperService.decodeToken(this.loginResponse.accessToken)
        const sub: Array<string> = token?.sub?.split('|') ?? []
        return sub.some((x) => x === 'evida_entra_id')
    }

    public setConfigId(id: ConfigId) {
        sessionStorage.setItem(AuthService.CONFIG_ID_STORAGE_KEY, id.toString())
    }

    public authorize(authOptions?: AuthOptions) {
        this.oidcSecurityService.authorize(this.configId, authOptions)
    }

    public logoffAndRevokeTokens() {
        this.oidcSecurityService.logoff(this.configId).subscribe((_) => {
            if (!this.isAuthenticated) {
                RedirectTo.loginLandingPage()
            }
        })
    }

    public logoffAndRevokeTokensLocal() {
        this.oidcSecurityService.logoffLocal(this.configId)
        RedirectTo.loginLandingPage()
    }

    public get enableProductionDevelopment(): boolean {
        return this.isBusinessCustomer && this.features.enableProductionDevelopment
    }

    public get isTimeDataConsumption(): boolean {
        if (this.isBusinessCustomer && this.features.enableTimedata) {
            // TO-DO: Find out how we know if business user has ConsumptionDevelopment
            let hasConsumptionDevelopment = true
            return hasConsumptionDevelopment
        }
        return false
    }

    public get userName(): string {
        const token = this.jwtHelperService.decodeToken(this.loginResponse.accessToken)
        return token?.['mitid.name'] ?? token?.['mitid.pro.name'] ?? ''
    }

    public get companyName(): string {
        const accessToken = this.loginResponse?.accessToken
        if (!accessToken) {
            return ''
        }

        const token = this.jwtHelperService.decodeToken(accessToken)
        return token?.['mitid.pro.org_name'] ?? ''
    }

    public get mitIdUuid(): string {
        const token = this.jwtHelperService.decodeToken(this.loginResponse.accessToken)
        return token['mitid.uuid'] ?? token['mitid.pro.uuid']
    }
}
